// ---------------------------------------------------------------------------
// How much screen is actually left.
//
// On a phone the on-screen keyboard takes roughly half the display and tells
// CSS nothing about it. `100vh`, `100dvh` and `window.innerHeight` all keep
// reporting the full window, so a layout built against any of them puts the
// board, the clock and the buttons underneath the keys the player is typing on.
// `window.visualViewport` is the only thing that knows: it reports the region
// that is genuinely visible, and it fires `resize` when the keyboard arrives and
// leaves.
//
// What this returns is deliberately not "is the keyboard up". Nothing can know
// that — a browser's own toolbar collapsing shrinks the visual viewport too, and
// so does a page zoom. It returns the height that is left and how much has been
// taken, and the screens decide what to do about it. `keyboardLikely` is offered
// as a reading rather than a fact, and is used for nothing safety-critical.
//
// `offsetTop` matters as much as the height. iOS does not resize the layout
// viewport for the keyboard, it SCROLLS it, so an element pinned with
// `position: fixed; bottom: 0` sits behind the keyboard rather than above it.
// A bar has to be positioned against `offsetTop + height`, which is what
// `bottomInset` works out.
//
// TWO INSETS, AND THE DIFFERENCE IS WHO IS ALREADY COMPENSATING. `bottomInset`
// is the older one and it answers "where is the bottom of the visible region,
// measured inside the layout viewport" — the right question for an element that
// is laid out in the layout viewport and stays there. `coveredHeight` answers
// "how much is covered", full stop, and it is the one to pin a `position: fixed`
// element with. See the comment on it: the two `bottomInset` terms it drops are
// both terms the browser has already applied by the time a fixed element is
// painted, and applying them a second time is what made a pinned bar slide up
// and down the screen as the page scrolled.
//
// Subscribed through `useSyncExternalStore` rather than mirrored into state
// from an effect, for the same reason `useMediaQuery` is: the effect version
// renders one frame of the wrong layout, and one frame of a board jumping under
// a thumb is exactly the thing this exists to stop.
// ---------------------------------------------------------------------------

import { useCallback, useSyncExternalStore } from 'react';

export interface ViewportReading {
  /** Height of the region actually on screen, in CSS pixels. */
  height: number;
  /** Width of the same region. */
  width: number;
  /**
   * How far to hold a bottom-pinned element up off the bottom of the LAYOUT
   * viewport so it sits on top of the keyboard rather than behind it. Zero when
   * nothing is covering anything.
   */
  bottomInset: number;
  /**
   * How much of the screen is covered, and nothing else. THIS is what a
   * `position: fixed` bar is held up by; `bottomInset` above is not.
   *
   * `bottomInset` is `window.innerHeight - height - offsetTop`, and both of the
   * terms this one drops are terms the browser has already applied by the time
   * a fixed element is painted:
   *
   *   - `offsetTop` is how far iOS has scrolled the visual viewport inside the
   *     layout viewport, and iOS carries fixed elements with it. Subtracting it
   *     again moves the bar a second time, in the same direction, so the bar
   *     rises and falls as the page is scrolled under the keyboard — which is
   *     exactly what it was reported doing.
   *   - `window.innerHeight` is not the box a fixed element resolves against.
   *     That box is the initial containing block, whose height is
   *     `document.documentElement.clientHeight`; on iOS `innerHeight` also moves
   *     as the toolbars collapse and expand, so a bar positioned from it drifts
   *     on an ordinary scroll with no keyboard involved at all.
   *
   * What is left changes only when something actually covers the screen, which
   * is the whole property a pinned bar needs. Clamping the old number would have
   * stopped it going negative without stopping it moving.
   */
  coveredHeight: number;
  /**
   * A reading, not a fact: something is covering enough of the screen that it is
   * probably a keyboard. Use it to choose a layout, never to decide whether an
   * answer counts.
   *
   * Letters play deliberately reads this for NOTHING any more: one phone layout,
   * built for the keyboard, is what stopped the screen reflowing around it. A
   * layout that swaps on a reading is a layout that jumps when the reading is
   * wrong.
   */
  keyboardLikely: boolean;
}

const NOTHING: ViewportReading = {
  height: 0,
  width: 0,
  bottomInset: 0,
  coveredHeight: 0,
  keyboardLikely: false,
};

/**
 * Below this share of the window, whatever is covering the screen is big enough
 * to be a keyboard. A browser toolbar collapsing takes far less; a keyboard on
 * every phone in circulation takes more.
 */
const KEYBOARD_SHARE = 0.75;

/**
 * The reading is cached and only rebuilt when a number in it changes, because
 * `useSyncExternalStore` compares snapshots by identity and a fresh object every
 * call is an infinite render loop. This is the one thing to be careful about if
 * you edit this file.
 */
let cached: ViewportReading = NOTHING;

/**
 * The height of the box a `position: fixed` element is laid out in.
 *
 * That is the initial containing block, which is the LAYOUT viewport, and
 * `document.documentElement.clientHeight` is its height. `window.innerHeight` is
 * a different number on the one platform this hook exists for: it grows and
 * shrinks as iOS collapses and restores its toolbars, so anything positioned
 * from it moves while the page is merely being scrolled.
 *
 * jsdom reports 0 for `clientHeight` because it does no layout, so the window is
 * the fallback and the tests read the same as the browser.
 */
function layoutHeight(): number {
  if (typeof window === 'undefined') return 0;
  return document.documentElement.clientHeight || window.innerHeight;
}

function read(): ViewportReading {
  const viewport = typeof window === 'undefined' ? undefined : window.visualViewport;
  if (!viewport) {
    // No support, or a bare test environment. Report the window and no inset,
    // which is the layout every screen had before this hook existed.
    const height = typeof window === 'undefined' ? 0 : window.innerHeight;
    const width = typeof window === 'undefined' ? 0 : window.innerWidth;
    if (cached.height === height && cached.width === width && cached.bottomInset === 0) {
      return cached;
    }
    cached = { height, width, bottomInset: 0, coveredHeight: 0, keyboardLikely: false };
    return cached;
  }

  // An iframe reports the TOP document's visual viewport while `innerHeight` is
  // its own, so the difference between them is the frame's position on the page
  // and has nothing to do with a keyboard. `/dev` is an iframe, and it read a
  // 420px inset on a desktop with no keyboard anywhere near it, which shoves
  // anything pinned clean off the screen. There is no reading to salvage here —
  // a frame cannot know — so it declines to guess and reports the frame.
  if (window.self !== window.top) {
    const height = window.innerHeight;
    const width = window.innerWidth;
    if (cached.height === height && cached.width === width && cached.bottomInset === 0) {
      return cached;
    }
    cached = { height, width, bottomInset: 0, coveredHeight: 0, keyboardLikely: false };
    return cached;
  }

  const height = viewport.height;
  const width = viewport.width;
  // What the layout viewport has that the visual one does not, minus however far
  // the page has been scrolled inside it. Clamped at zero: a viewport taller
  // than the window is a rounding artefact, not a negative inset.
  const inset = window.innerHeight - height - viewport.offsetTop;
  const bottomInset = inset > 1 ? inset : 0;
  // The same subtraction with both scroll-dependent terms taken out. See the
  // note on `coveredHeight`: this is the one a pinned bar uses, and the point of
  // it is that scrolling cannot change it.
  const gone = layoutHeight() - height;
  const coveredHeight = gone > 1 ? gone : 0;
  const keyboardLikely = window.innerHeight > 0 && height / window.innerHeight < KEYBOARD_SHARE;

  if (
    cached.height === height &&
    cached.width === width &&
    cached.bottomInset === bottomInset &&
    cached.coveredHeight === coveredHeight &&
    cached.keyboardLikely === keyboardLikely
  ) {
    return cached;
  }
  cached = { height, width, bottomInset, coveredHeight, keyboardLikely };
  return cached;
}

export function useVisualViewport(): ViewportReading {
  const subscribe = useCallback((onChange: () => void) => {
    const viewport = typeof window === 'undefined' ? undefined : window.visualViewport;
    if (!viewport) {
      if (typeof window === 'undefined') return () => {};
      window.addEventListener('resize', onChange);
      return () => window.removeEventListener('resize', onChange);
    }
    // Both events. `resize` is the keyboard arriving; `scroll` is iOS moving the
    // visual viewport under a keyboard that is already up, which changes
    // `offsetTop` and therefore where a pinned bar belongs, without changing the
    // height at all.
    viewport.addEventListener('resize', onChange);
    viewport.addEventListener('scroll', onChange);
    return () => {
      viewport.removeEventListener('resize', onChange);
      viewport.removeEventListener('scroll', onChange);
    };
  }, []);

  return useSyncExternalStore(subscribe, read, () => NOTHING);
}
